import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { programService } from '../../services/programService';
import ProgramStatsCards from './components/ProgramStatsCards';
import ProgramDirectory from './components/ProgramDirectory';

const ITEMS_PER_PAGE = 6;

const ProgramManager: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch program stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['programStats'],
    queryFn: () => programService.getProgramStats(),
  });

  // Fetch all programs
  const { data: programs, isLoading: programsLoading } = useQuery({
    queryKey: ['programs'],
    queryFn: () => programService.getAllPrograms(),
  });

  const handleProgramClick = (programId: string) => {
    navigate(`/programs/${programId}`);
  };

  const handleCreateNew = () => {
    navigate('/programs/create');
  };

  // Pagination calculations
  const totalItems = programs?.length || 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedPrograms = programs?.slice(startIndex, endIndex) || [];

  // Reset to page 1 when programs change
  useEffect(() => {
    setCurrentPage(1);
  }, [programs?.length]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 4;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        for (let i = 1; i <= maxPagesToShow; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 1) {
        for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 1; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    return pages;
  };

  return (
    <div className="max-w-[1920px] mx-auto px-6 py-8">
      {/* Page Header - Height: 80px, Padding: 24px horizontal */}
      <div className="flex items-center justify-between h-20 pb-6 border-b border-[#1F1F1F] mb-8">
        <h1 className="text-4xl font-bold text-[#23F47C] mb-2">
          Program Manager
        </h1>
        <button
          onClick={handleCreateNew}
          className="inline-flex items-center gap-2 h-11 px-6 bg-[#23F47C] text-[#0A0A0A] text-base font-semibold rounded-lg hover:bg-[#1FD66D] transition-all duration-200"
        >
          <Plus size={20} />
          <span>Create Program</span>
        </button>
      </div>

      {/* Stats Cards - Height: 120px, Gap: 24px, Margin-bottom: 32px */}
      <div className="mb-8">
        <ProgramStatsCards stats={stats || null} isLoading={statsLoading} />
      </div>

      {/* Program Directory Grid - Padding: 24px horizontal */}
      {totalItems === 0 ? (
        <ProgramDirectory
          programs={[]}
          isLoading={programsLoading}
          onProgramClick={handleProgramClick}
          onCreateNew={handleCreateNew}
        />
      ) : (
        <>
          <ProgramDirectory
            programs={paginatedPrograms}
            isLoading={programsLoading}
            onProgramClick={handleProgramClick}
            onCreateNew={handleCreateNew}
          />

          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-8">
            <div className="text-gray-400 text-sm">
              Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} programs
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft size={20} />
              </button>

              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                    currentPage === page
                      ? 'bg-[#23F47C] text-gray-900'
                      : 'border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C]'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProgramManager;
